import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const AdminEmailTemplateEditPage = () => {
  // Template edit page with proper variable escaping
  const { sequenceId, templateId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [sequence, setSequence] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    step_number: 1,
    delay_days: 0,
    is_active: true
  });

  const isCreating = !templateId || templateId === 'create';

  const apiCall = async (url, options = {}) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  useEffect(() => {
    loadSequence();
    if (!isCreating) {
      loadTemplate();
    }
  }, [sequenceId, templateId]);

  const loadSequence = async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/api/v1/admin/email-sequences/sequences/${sequenceId}`);
      setSequence(data.sequence);
      
      // If creating a new template, set the next available step number
      if (isCreating && data.sequence.templates) {
        const maxStepNumber = data.sequence.templates.length > 0 
          ? Math.max(...data.sequence.templates.map(t => t.step_number))
          : 0;
        
        setFormData(prev => ({
          ...prev,
          step_number: maxStepNumber + 1
        }));
      }
    } catch (err) {
      setError(`Failed to load sequence: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/api/v1/admin/email-sequences/templates/${templateId}`);
      const template = data.template;
      
      setFormData({
        name: template.name || '',
        subject: template.subject || '',
        content: template.content || '',
        step_number: template.step_number || 1,
        delay_days: template.delay_days || 0,
        is_active: template.is_active !== false
      });
    } catch (err) {
      setError(`Failed to load template: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      if (isCreating) {
        await apiCall(`/api/v1/admin/email-sequences/sequences/${sequenceId}/templates`, {
          method: 'POST',
          body: JSON.stringify(formData)
        });
      } else {
        await apiCall(`/api/v1/admin/email-sequences/templates/${templateId}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
      }
      
      // Navigate back to templates list
      navigate(`/admin/email-sequences/${sequenceId}/templates`);
    } catch (err) {
      setError(`Failed to ${isCreating ? 'create' : 'update'} template: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {isCreating ? 'Create Template' : 'Edit Template'}
              </h1>
              {sequence && (
                <p className="text-sm text-muted-foreground mt-1">Sequence: {sequence.name}</p>
              )}
            </div>
            <Link 
              to={`/admin/email-sequences/${sequenceId}/templates`} 
              className="btn btn-secondary"
            >
              ← Back to Templates
            </Link>
          </div>
        </div>

        <div className="card-body">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex">
                <div className="text-red-600 text-sm">{error}</div>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto text-red-400 hover:text-red-600"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">Template Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Welcome Email, Follow-up #1"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">Step Number</label>
                <input
                  type="number"
                  value={formData.step_number}
                  onChange={(e) => handleChange('step_number', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">Delay (days)</label>
                <input
                  type="number"
                  value={formData.delay_days}
                  onChange={(e) => handleChange('delay_days', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  placeholder="0"
                />
                <p className="text-sm text-muted-foreground mt-1">Days to wait before sending this email</p>
              </div>
              
              <div className="flex items-center pt-8">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-border rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-card-foreground">
                  Active
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">Subject Line</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Use {{user_name}}, {{app_url}}, etc."
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">Email Content</label>
              <textarea
                value={formData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="12"
                placeholder="Email content... Use {{user_name}}, {{user_email}}, {{app_url}}, {{support_email}}, etc."
                required
              />
            </div>

            <div className="text-sm text-muted-foreground bg-background p-4 rounded-md">
              <p className="font-medium mb-2">Available Variables:</p>
              <p>{`{{user_name}}, {{user_email}}, {{app_url}}, {{support_email}}, {{company_name}}, {{signup_date}}`}</p>
            </div>
            
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <Link 
                to={`/admin/email-sequences/${sequenceId}/templates`}
                className="btn btn-secondary"
              >
                Cancel
              </Link>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : (isCreating ? 'Create Template' : 'Update Template')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminEmailTemplateEditPage;